import { PAGE_BG_STYLE } from "@/lib/pageBg";
import { useState, useEffect } from "react";
import { Loader2, RefreshCw, BarChart3, Users, Image, Wallet, TrendingUp, Activity } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAnalytics } from "@/hooks/useAnalytics";
import { useWalletSafe } from "@/hooks/useWalletSafe";
import { useWalletSupabaseSession } from "@/hooks/useWalletSupabaseSession";
import { supabase } from "@/integrations/supabase/client";
import AppHeader from "@/components/AppHeader";
import Footer from "@/components/Footer";
import { PageHero } from "@/components/PageHero";

import {
  StatCard,
  AnalyticsChart,
  NFTAnalyticsTable,
  UserAnalyticsTable,
  TrendingNFTsCard,
  MyAnalyticsSummary,
} from "@/components/analytics";
import { priceToUsd, formatUsdPrice } from "@/lib/priceUtils";
import { useSolPrice } from "@/hooks/useSolPrice";

export default function Analytics() {
  const { connected, publicKey } = useWalletSafe();
  const { status: sessionStatus } = useWalletSupabaseSession();
  const { usdToSol } = useSolPrice();
  const [isAdmin, setIsAdmin] = useState(false);
  const [checkingAdmin, setCheckingAdmin] = useState(true);

  useEffect(() => {
    const checkAdmin = async () => {
      if (!connected || sessionStatus !== "valid") {
        setIsAdmin(false);
        setCheckingAdmin(false);
        return;
      }
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user?.id) {
        setIsAdmin(false);
        setCheckingAdmin(false);
        return;
      }
      const { data } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", session.user.id)
        .eq("role", "admin")
        .maybeSingle();
      setIsAdmin(!!data);
      setCheckingAdmin(false);
    };
    checkAdmin();
  }, [connected, sessionStatus]);

  const { platformStats, nftAnalytics, userAnalytics, trendingNFTs, dailyStats, myAnalytics, myNFTs, myDailyStats, loading, refresh } = useAnalytics(isAdmin);

  const chartData = dailyStats.map(d => ({ date: d.date, value: d.nfts_created }));

  // Format volume: USD primary, SOL secondary
  const formatVolumeDisplay = (volume: number) => {
    const usdValue = priceToUsd(volume);
    const solValue = usdToSol(usdValue);
    return `${formatUsdPrice(usdValue)} ≈ ${solValue.toFixed(4)} SOL`;
  };

  if (checkingAdmin || loading) {
    return (
      <div className="min-h-screen relative flex items-center justify-center" style={PAGE_BG_STYLE}>
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen relative flex flex-col" style={PAGE_BG_STYLE}>
      <AppHeader currentPage="analytics" />
      <main className="flex-1 pt-20 pb-24 px-4">
        <div className="max-w-7xl mx-auto space-y-4">
          <div className="flex items-center justify-between">
            <PageHero
              title="Analytics"
              subtitle={isAdmin ? "Platform statistics and metrics" : "Your NFT performance and earnings"}
              showLogo={false}
            />
            <Button variant="outline" onClick={refresh} className="border-border hover:bg-muted">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>

          {isAdmin ? (
            <Tabs defaultValue="overview">
              <TabsList className="mb-6">
                <TabsTrigger value="overview">Overview</TabsTrigger>
                <TabsTrigger value="nfts">NFTs</TabsTrigger>
                <TabsTrigger value="users">Users</TabsTrigger>
                <TabsTrigger value="my">My Stats</TabsTrigger>
              </TabsList>

              <TabsContent value="overview" className="space-y-6">
                {platformStats && (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                    <StatCard title="Total NFTs" value={platformStats.total_nfts} icon={Image} />
                    <StatCard title="Listed" value={platformStats.listed_nfts} icon={BarChart3} />
                    <StatCard title="Total Users" value={platformStats.total_users} icon={Users} />
                    <StatCard title="Total Sales" value={platformStats.total_sales} icon={TrendingUp} />
                    <StatCard title="Volume" value={formatVolumeDisplay(platformStats.total_volume)} icon={Wallet} />
                    <StatCard title="NFTs (24h)" value={platformStats.nfts_24h} icon={Activity} trend={{ value: platformStats.nfts_24h, label: "new", positive: true }} />
                  </div>
                )}
                <div className="grid md:grid-cols-2 gap-6">
                  <AnalyticsChart title="NFTs Created (30 days)" data={chartData} type="bar" valueLabel="NFT" />
                  <TrendingNFTsCard data={trendingNFTs} />
                </div>
              </TabsContent>

              <TabsContent value="nfts">
                <NFTAnalyticsTable data={nftAnalytics} />
              </TabsContent>

              <TabsContent value="users">
                <UserAnalyticsTable data={userAnalytics} />
              </TabsContent>

              <TabsContent value="my">
                <MyAnalyticsSummary user={myAnalytics} nfts={myNFTs} dailyStats={myDailyStats} />
              </TabsContent>
            </Tabs>
          ) : (
            <MyAnalyticsSummary user={myAnalytics} nfts={myNFTs} dailyStats={myDailyStats} />
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
